// calls all apis for ya

import Axios from 'axios';
import rootStore from '../store';
import { apiBaseUrl, tokenForceReloginThresholdMins, tokenRefreshBufferMins } from '../config';
import { get, isEmpty, isEqual } from 'lodash';
import { constants } from '../constants/configuration';
import {
  internetDisconnectivityHandledAPIEndpointBlacklist,
  internetErrorResponseStoreDispatchDelay,
  serverErrorCodes,
  tokenExcludedAPIEndpointBlacklist,
} from '../constants/globals';
import { apiErrorResponseAction } from '../actions/common';
import {
  refreshTokenAction,
  resolveAuthRefreshContext,
  updateAuthTokensAction,
} from '../actions/authTokens';
import {
  checkInternetConnectivity,
  getIsApiRequestOnHold,
  getJWTRemainingTimeMs,
  isJWTTokenExpiringSoon,
  isJWTTokenExpired,
  setIsApiRequestOnHold,
} from './helpers';

const { store, persistor } = rootStore;

const TYPE_JSON = 'application/json';

let pendingRequests = [];
let refreshPromise = null;

const getActiveAccessToken = (state) =>
  get(state, 'restaurant.authData.accessToken', '') ||
  get(state, 'authentication.authData.accessToken', '') ||
  get(state, 'fudr.authData.accessToken', '');

export const buildAPIInstance = ({ contenttype = TYPE_JSON }) => {
  const headers = {
    common: {
      Accept: TYPE_JSON,
      'Content-Type': contenttype,
      'x-source-type': 'admin',
    },
    post: {
      'Content-Type': TYPE_JSON,
    },
  };
  const objAxios = Axios.create({
    baseURL: apiBaseUrl,
    headers: headers,
  });

  const shouldSkipAuthHeader = (url) =>
    tokenExcludedAPIEndpointBlacklist.some((endpoint) => String(url || '').includes(endpoint));

  const isLogoutEndpoint = (url) => {
    const u = String(url || '');
    return u.includes('/restaurants/logout') || u.includes('/restaurants/logoutAllDevices');
  };

  const forceReloginThresholdMs = tokenForceReloginThresholdMins * 60 * 1000;
  const NO_TOKEN_REMAINING_MS = null;

  // While refresh runs, extra calls are queued. On success, resolution must match the interceptor:
  // request interceptor — resolve with `config` so Axios continues that outbound request;
  // response interceptor — the returned promise is what `axios(...)` awaits, so replay the request
  // and resolve/reject with the real response (resolving with `config` would look like a bogus success).
  const enqueueRequestWhileOnHold = (config, fromResponse = false) =>
    new Promise((resolve, reject) => {
      pendingRequests.push({ resolve, reject, config, fromResponse });
    });

  const handlePendingRequests = (params) => {
    const newToken = params && params.newToken;
    const error = params && params.error;
    if (newToken) {
      for (const request of pendingRequests) {
        try {
          if (typeof request.resolve !== 'function' || !request.config) {
            console.warn('Skipped malformed pending request (resolve):', request);
            continue;
          }
          if (!request.config.headers) {
            request.config.headers = {};
          }
          request.config.headers['Authorization'] = `Bearer ${newToken}`;
          if (request.fromResponse) {
            // Replay so the original axios() call settles with a real response or error.
            objAxios(request.config).then(request.resolve).catch(request.reject);
          } else {
            // Request pipeline: fulfilled interceptor value is the next config.
            request.resolve(request.config);
          }
        } catch (err) {
          console.error('Failed to resolve pending request:', err, request);
        }
      }
    } else {
      for (const request of pendingRequests) {
        try {
          if (typeof request.reject === 'function') {
            request.reject(error);
          } else {
            console.warn('Skipped malformed pending request (reject):', request);
          }
        } catch (err) {
          console.error('Failed to reject pending request:', err, request);
        }
      }
    }

    // Clear after processing all
    pendingRequests = [];
  };

  const handleLogoutError = (error) => {
    const errorData = get(error, 'response.data', error);
    if (!errorData) {
      console.error('No error data found in error object:', error);
      return;
    }

    try {
      if (persistor.getState().bootstrapped) {
        store.dispatch(apiErrorResponseAction(errorData));
      } else {
        const unsubscribe = persistor.subscribe(() => {
          if (persistor.getState().bootstrapped) {
            unsubscribe(); // Stop listening after bootstrapped
            store.dispatch(apiErrorResponseAction(errorData));
          }
        });
      }
    } catch (e) {
      store.dispatch(apiErrorResponseAction(errorData));
    }
  };

  const handleTokenRefresh = async () => {
    if (refreshPromise) return refreshPromise;

    refreshPromise = (async () => {
      try {
        const storedState = store && store.getState();
        const { context, refreshToken } = resolveAuthRefreshContext(storedState);

        if (!refreshToken || isJWTTokenExpired(refreshToken)) {
          return { isError: true, terminal: true, context };
        }

        const res = await store.dispatch(refreshTokenAction());
        const nextAccessToken = get(res, 'accessToken', '');
        const nextRefreshToken = get(res, 'refreshToken', '');
        const resContext = get(res, 'context', context);

        if (!nextAccessToken || !nextRefreshToken) {
          const status = get(res, 'error.response.status', null);
          const terminal = status === 401 || status === 403;
          return { isError: true, error: get(res, 'error'), terminal, context: resContext };
        }

        const stateAfter = store.getState();
        const rtNow =
          context === 'restaurant'
            ? get(stateAfter, 'restaurant.authData.refreshToken', '')
            : get(stateAfter, 'fudr.authData.refreshToken', '');
        if (rtNow !== refreshToken) {
          return { discarded: true };
        }

        // Await dispatch so state/header updates complete before returning tokens to callers.
        await store.dispatch(
          updateAuthTokensAction({
            accessToken: nextAccessToken,
            refreshToken: nextRefreshToken,
            context: resContext,
          }),
        );

        return {
          accessToken: nextAccessToken,
          refreshToken: nextRefreshToken,
          context: resContext,
        };
      } catch (e) {
        const status = get(e, 'response.status', null);
        const terminal = status === 401 || status === 403;
        return { isError: true, error: e, terminal };
      } finally {
        refreshPromise = null;
      }
    })();

    return refreshPromise;
  };

  const handleAuthenticationError = async (error) => {
    setIsApiRequestOnHold(true);

    try {
      const tokenRes = await handleTokenRefresh();
      if (tokenRes && tokenRes.discarded) {
        handlePendingRequests({ error: new Error('Session ended') });
        return null;
      }
      const newToken = get(tokenRes, 'accessToken', '');

      if (newToken) {
        handlePendingRequests({ newToken });
        return newToken;
      }

      const err = get(tokenRes, 'error', error);
      handlePendingRequests({ error: err || error });
      handleLogoutError({
        response: {
          data: {
            isError: true,
            code: serverErrorCodes?.invalidToken,
            message: 'Authentication error',
            detail: 'Authentication error',
            status: 'UNAUTHORIZED',
          },
        },
      });
      return null;
    } finally {
      setIsApiRequestOnHold(false);
    }
  };

  const forceRelogin = () => {
    handleLogoutError({
      response: {
        data: {
          isError: true,
          code: serverErrorCodes?.invalidToken,
          message: 'Authentication error',
          detail: 'Authentication error',
          status: 'UNAUTHORIZED',
        },
      },
    });
  };

  const getCurrentAccessTokenRemainingMs = () => {
    const latestAccessToken = getActiveAccessToken(store.getState());
    if (isEmpty(latestAccessToken)) {
      return NO_TOKEN_REMAINING_MS;
    }
    return getJWTRemainingTimeMs(latestAccessToken);
  };

  const shouldForceReloginForLowRemainingTime = (remainingMs) =>
    remainingMs !== NO_TOKEN_REMAINING_MS && remainingMs <= forceReloginThresholdMs;

  objAxios.interceptors.request.use(async (config) => {
    if (!config.url) {
      return config;
    }

    const isApiRequestOnHold = getIsApiRequestOnHold();
    // Queue only while requests are on hold (during token refresh).
    // Auth-exempt URLs (refresh/signin/logout/etc.) must bypass queueing, otherwise refresh can
    // block itself and pendingRequests will not drain until refresh completes.
    if (isApiRequestOnHold && !shouldSkipAuthHeader(config.url)) {
      return enqueueRequestWhileOnHold(config);
    }

    const storedState = store && store.getState();
    const accessToken = getActiveAccessToken(storedState);

    if (!isEmpty(accessToken) && !shouldSkipAuthHeader(config.url)) {
      const isExpired = isJWTTokenExpired(accessToken);
      const isExpiringSoon = isJWTTokenExpiringSoon(accessToken, tokenRefreshBufferMins);

      if (isExpired) {
        const authError = Object.assign(new Error('Authentication error'), {
          config,
          response: {
            data: {
              isError: true,
              code: serverErrorCodes?.invalidToken,
              message: 'Authentication error',
              detail: 'Authentication error',
              status: 'UNAUTHORIZED',
            },
          },
        });
        const newToken = await handleAuthenticationError(authError);
        if (!newToken) {
          if (authError.config) {
            // This error is intentionally synthetic (created in the request interceptor).
            // If refresh fails we reject it, which routes into the response error interceptor.
            // Marking `_retry` here prevents a second refresh attempt and duplicate logout dispatch.
            authError.config._retry = true;
          }
          return Promise.reject(authError);
        }
        config.headers['Authorization'] = `Bearer ${newToken}`;
      } else {
        if (isExpiringSoon && !refreshPromise) {
          (async () => {
            const res = await handleTokenRefresh();
            const remainingMs = getCurrentAccessTokenRemainingMs();
            // `handleTokenRefresh` returns either:
            // - success (tokens, no `isError`)
            // - `{ isError: true, terminal?: boolean, error }`
            // - `{ discarded: true }` when another refresh superseded this attempt
            //
            // Treat unexpected falsy/non-object results as failures; otherwise refresh can silently
            // no-op and leave us sending requests with an about-to-expire token.
            if (!res || typeof res !== 'object') {
              // If we are within the last couple minutes, prefer a forced relogin over continuing
              // with a potentially invalid session.
              if (shouldForceReloginForLowRemainingTime(remainingMs)) {
                forceRelogin();
                return;
              }
              console.error('[auth:refreshToken] expiring-soon refresh returned empty result (ignored)', {
                res,
              });
              return;
            }

            // Another refresh won the race and replaced tokens; ignore this stale result.
            if (res.discarded) return;

            // Success.
            if (!res.isError) return;

            if (res.terminal || shouldForceReloginForLowRemainingTime(remainingMs)) {
              forceRelogin();
              return;
            }

            console.error('[auth:refreshToken] expiring-soon refresh failed (ignored)', {
              status: get(res, 'error.response.status', null),
            });
          })().catch((e) => {
            const remainingMs = getCurrentAccessTokenRemainingMs();
            if (shouldForceReloginForLowRemainingTime(remainingMs)) {
              forceRelogin();
              return;
            }
            console.error('[auth:refreshToken] expiring-soon refresh threw (ignored)', e);
          });
        }
        config.headers['Authorization'] = `Bearer ${accessToken}`;
      }
    }

    const requestURLPart = config.url;
    const isRequestURLBlacklisted = internetDisconnectivityHandledAPIEndpointBlacklist.some(
      (endpoint) => requestURLPart.indexOf(endpoint) > -1,
    );
    if (!isRequestURLBlacklisted) {
      checkInternetConnectivity().then((isOnline) => {
        if (!isOnline) {
          const errorResponse = {
            message: constants.globalErrorInfo.internetDisconnectivityErrorMessage,
          };
          setTimeout(() => {
            store.dispatch(apiErrorResponseAction(errorResponse));
          }, internetErrorResponseStoreDispatchDelay);
        }
      });
    }

    return config;
  });

  objAxios.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (!error) {
        return Promise.reject(new Error('Unknown response error'));
      }

      const originalRequest = error.config;

      const isInvalidTokenCode = isEqual(get(error, 'response.data.code'), serverErrorCodes?.invalidToken);
      const isUnauthorized = isEqual(get(error, 'response.status'), 401);

      if ((isInvalidTokenCode || isUnauthorized) && isLogoutEndpoint(originalRequest?.url)) {
        return Promise.reject(error);
      }

      if ((isInvalidTokenCode || isUnauthorized) && shouldSkipAuthHeader(originalRequest?.url)) {
        return Promise.reject(error);
      }

      if ((isInvalidTokenCode || isUnauthorized) && originalRequest && !originalRequest._retry) {
        originalRequest._retry = true;

        const isApiRequestOnHold = getIsApiRequestOnHold();
        if (isApiRequestOnHold) {
          return enqueueRequestWhileOnHold(originalRequest, true); // response-queue path; see enqueueRequestWhileOnHold
        }

        const newToken = await handleAuthenticationError(error);
        if (newToken && originalRequest.headers) {
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          return objAxios(originalRequest);
        }
      }
      return Promise.reject(error);
    },
  );

  return objAxios;
};

const client = (params = {}) => buildAPIInstance(params);

export default client;
